import React, { useEffect, useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { X, Upload, Check, Loader2, Image as ImageIcon, Film, FileText } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative: a module the Jest suites resolve for real (its own "@/lib/api" is mocked).
import { resolveApiUrl } from "../pathways/learnerApi";

/** Files above this size go through the chunked upload (the proxy caps one request body). */
export const CHUNK_SIZE = 5 * 1024 * 1024;

/**
 * What the picker offers, per `accept`:
 *   input     the file input's `accept` attribute
 *   matches   which library assets it lists (and which uploaded asset it accepts)
 *   copy      the key family of its words ("media.picker.*" for images, the historical
 *             copy; "media.picker.<accept>.*" for the others)
 *   visibility what an upload from this mode is stored as. Images feed public pages
 *             (site, blog, testimonials): they send nothing and keep the server's
 *             default, `public`, and the image mode never lists a private asset. Videos, PDFs and
 *             resources are pathway step content, which only the practitioner (through
 *             the authenticated admin route) and enrolled learners (through the gated
 *             learner route) may read, so they are uploaded `private`: the public
 *             `/api/media/file/{id}` refuses them.
 *
 * `image` keeps the picker's original behaviour: every non-video (public) asset is listed.
 * `pdf` and `resource` follow the media kinds of the pathways contract (§7): a reading's
 * PDF is a `document` with `application/pdf`; a downloadable resource is a `document` or
 * an `image`.
 */
const ACCEPTS = Object.freeze({
  image: {
    input: "image/*",
    // A private asset is never offered here: image callers store the URL in public pages.
    matches: (it) => it.kind !== "video" && it.visibility !== "private",
    copy: "media.picker",
    icon: ImageIcon,
    visibility: null,
  },
  video: { input: "video/*", matches: (it) => it.kind === "video", copy: "media.picker.video", icon: Film, visibility: "private" },
  pdf: {
    input: "application/pdf",
    matches: (it) => String(it.content_type || "").toLowerCase() === "application/pdf",
    copy: "media.picker.pdf",
    icon: FileText,
    visibility: "private",
  },
  resource: {
    input: "application/pdf,.docx,.xlsx,.pptx,text/plain,image/*",
    matches: (it) => it.kind === "document" || it.kind === "image",
    copy: "media.picker.resource",
    icon: FileText,
    visibility: "private",
  },
});

/**
 * Uploads one file to the media library and resolves the stored asset
 * (`{id, url, original_name, content_type, kind, size, visibility, ...}`). A file above
 * CHUNK_SIZE is sent in chunks (init → PUT each chunk → complete), as the media library
 * itself does (admin/MediaAdmin.jsx), because one request body that large is refused
 * upstream. `visibility` travels on every path that creates the asset: a form field of the
 * simple upload, and a body field of both chunk calls.
 *
 * @param {File} file
 * @param {(percent: number) => void} [onProgress]
 * @param {{ visibility?: "public"|"private" }} [options]  omitted = the server's default (public)
 */
export async function uploadMediaFile(file, onProgress = () => {}, { visibility } = {}) {
  const extra = visibility ? { visibility } : {};
  if (file.size <= CHUNK_SIZE) {
    const fd = new FormData();
    fd.append("file", file);
    if (visibility) fd.append("visibility", visibility);
    const { data } = await api.post("/admin/media", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => e.total && onProgress(Math.round((100 * e.loaded) / e.total)),
    });
    return data;
  }
  const { data: init } = await api.post("/admin/media/chunk/init", { filename: file.name, content_type: file.type, ...extra });
  let sent = 0;
  for (let off = 0; off < file.size; off += CHUNK_SIZE) {
    const blob = file.slice(off, off + CHUNK_SIZE);
    // Sequential on purpose: the server appends chunks in arrival order.
    // eslint-disable-next-line no-await-in-loop
    await api.put(`/admin/media/chunk/${init.upload_id}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
    sent += blob.size;
    onProgress(Math.round((100 * sent) / file.size));
  }
  const { data } = await api.post(`/admin/media/chunk/${init.upload_id}/complete`, { filename: file.name, content_type: file.type, ...extra });
  return data;
}

/**
 * The address the BROWSER fetches an asset from (an `<img>`, a `<video>`, a link).
 *
 *   public   `/api/media/file/{id}`: anonymous, prefixed with the backend origin as the
 *            picker always did (these are the URLs image callers store in site content)
 *   private  `/api/admin/media/{id}/file`: authenticated by the session cookie and
 *            tenant-scoped, so it is resolved like every browser-fetched API address
 *            (learnerApi.resolveApiUrl keeps the `/api/t/{slug}` prefix on the platform
 *            host, where no `X-Tenant-Slug` header can ride on a media element)
 *
 * @param {{ url: string, visibility?: string }} asset
 */
export const assetUrl = (asset) => {
  const path = String(asset.url || "");
  if (asset.visibility === "private") return resolveApiUrl(path);
  return path.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;
};

/**
 * Modal to pick an asset from the Media Library (GET /admin/media) or upload a new one.
 *
 * `onSelect(url, asset)`: `url` is the asset's browser address (`assetUrl`: what image
 * callers store);
 * `asset` is the library row itself, for callers that store the media id instead (the
 * pathway step editor).
 *
 * @param {object}  props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(url: string, asset: object) => void} props.onSelect
 * @param {"image"|"video"|"pdf"|"resource"} [props.accept]  default "image"
 */
export default function MediaPickerModal({ isOpen, onClose, onSelect, accept = "image" }) {
  const t = useT("admin");
  const mode = ACCEPTS[accept] || ACCEPTS.image;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/media");
      setItems((Array.isArray(data) ? data : []).filter(mode.matches));
    } catch (e) {
      setError(extractApiError(e, t("media.picker.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [t, mode]);

  useEffect(() => {
    if (isOpen) loadMedia();
  }, [isOpen, loadMedia]);

  if (!isOpen) return null;

  const pick = (asset) => {
    onSelect(assetUrl(asset), asset);
    onClose();
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setError("");
    try {
      const asset = await uploadMediaFile(file, setProgress, { visibility: mode.visibility });
      if (!mode.matches(asset)) {
        // Stored, but not what this field takes (a .mov sent as an octet stream, say):
        // it stays in the library, and the field is not given it.
        // (The library is re-read first: its loader clears the error line.)
        await loadMedia();
        setError(t(`${mode.copy}.wrongType`));
        return;
      }
      pick(asset);
    } catch (e) {
      setError(extractApiError(e, t("media.picker.uploadFailed")));
    } finally {
      setUploading(false);
    }
  };

  const Icon = mode.icon;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="media-picker-modal">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-picker-title"
        className="bg-white border border-eh-border-soft rounded-3xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-eh-border-soft flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="text-eh-sage-deep" size={20} />
            <h3 id="media-picker-title" className="font-display text-xl text-eh-ink">{t("media.libraryTitle")}</h3>
          </div>
          <button onClick={onClose} aria-label={t("media.picker.cancel")} className="p-1.5 rounded-full text-eh-muted hover:bg-eh-bg hover:text-eh-ink">
            <X size={18} />
          </button>
        </div>

        {/* Upload bar */}
        <div className="px-6 py-3 bg-eh-cream/50 border-b border-eh-border-soft flex items-center justify-between gap-4">
          <span className="text-xs text-eh-muted">{t(`${mode.copy}.hint`)}</span>
          <label className="btn-primary text-xs cursor-pointer inline-flex items-center gap-1.5" data-testid="media-picker-upload-label">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading
              ? `${t("media.picker.uploading")}${progress > 0 ? ` ${progress}%` : ""}`
              : t(`${mode.copy}.upload`)}
            <input
              type="file"
              accept={mode.input}
              disabled={uploading}
              onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              className="hidden"
              data-testid="media-picker-upload-input"
            />
          </label>
        </div>

        {error && <div role="alert" className="mx-6 mt-3 text-xs text-red-600 bg-red-50 p-2.5 rounded-xl">{error}</div>}

        {/* Media Grid */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-16 text-center text-eh-muted text-sm flex flex-col items-center gap-2">
              <Loader2 size={24} className="animate-spin text-eh-sage-deep" />
              {t("media.picker.loading")}
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-eh-muted text-sm bg-eh-cream/30 border border-dashed border-eh-border-soft rounded-2xl p-8">
              {t(`${mode.copy}.empty`)}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {items.map((it) => {
                const url = assetUrl(it);
                const isImage = it.kind === "image" || (accept === "image" && it.kind !== "document");
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => pick(it)}
                    data-testid={`media-pick-item-${it.id}`}
                    aria-label={t("media.picker.selectNamed", { name: it.original_name })}
                    className="group relative aspect-square bg-eh-cream rounded-2xl overflow-hidden border border-eh-border-soft hover:border-eh-sage-deep hover:ring-2 hover:ring-eh-sage-deep/30 transition text-left"
                  >
                    {isImage ? (
                      <img src={url} alt={it.original_name} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" loading="lazy" />
                    ) : (
                      <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center">
                        {it.kind === "video" ? <Film size={28} className="text-eh-sage-deep" /> : <FileText size={28} className="text-eh-sage-deep" />}
                        <span className="text-xs text-eh-ink break-all line-clamp-3">{it.original_name}</span>
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-medium gap-1">
                      <Check size={16} /> {t("media.picker.select")}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-eh-border-soft bg-eh-cream/30 flex justify-end">
          <button onClick={onClose} className="btn-ghost text-xs">
            {t("media.picker.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
